import { Button, Checkbox, Form, Radio, Spin, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import {
  INVOICE_TYPE,
  type PagoFifoPreviewCoveredInvoice,
  type PagoFifoPreviewInvoice,
} from '@aid/shared';
import { useT } from '../../../locales';
import { formatCurrency } from '../../../lib/format';

const { Text } = Typography;

/**
 * How a NORMAL payment is applied once the operator decides to pay debt
 * instead of buying a package: over every open invoice oldest-first (FIFO), or
 * over exactly the invoices they tick. Neither is preselected.
 */
export const MODO_FACTURAS = {
  FIFO: 'FIFO',
  PUNTUAL: 'PUNTUAL',
} as const;

export type ModoFacturas = (typeof MODO_FACTURAS)[keyof typeof MODO_FACTURAS];

/**
 * Keeps only the client's OPEN invoices the targeted payment can name. Invoices
 * the endpoint does not type are never offered: the prepaid screen must not
 * risk sending an unknown invoice as a targeted payment.
 */
export function filtrarFacturasPagables(
  facturas: readonly PagoFifoPreviewInvoice[] | undefined,
): PagoFifoPreviewInvoice[] {
  return (facturas ?? []).filter(
    (factura) =>
      factura.tipoFactura === INVOICE_TYPE.PERIODICA ||
      factura.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA,
  );
}

interface PagoFacturasPickerProps {
  /** Open invoices payable with this payment. */
  facturas: readonly PagoFifoPreviewInvoice[];
  /**
   * Open invoices a LIVE prepaid package settles. They are shown, disabled and
   * with the covering package, so the operator understands why they cannot be
   * selected — paying them would charge the same period twice.
   */
  cubiertas?: readonly PagoFifoPreviewCoveredInvoice[];
  /** The invoice lookup is still running: nothing can be chosen yet. */
  loading?: boolean;
  disabled?: boolean;
  /** NORMAL with payable invoices: a mode must be chosen, none is preselected. */
  modoRequerido: boolean;
  /** Sum of the marked invoices' balances: the amount ceiling. */
  totalSeleccionado: number;
  /** Amount the operator typed: it decides the surplus notice. */
  montoIngresado: number;
  /** Choosing either mode means this is a NORMAL payment of the client debt. */
  onModeChange: () => void;
  /** Ticking an invoice also means this is a NORMAL payment. */
  onSelectionChange: () => void;
}

/**
 * A PREPAGO client's open invoices. The operator first chooses HOW the payment
 * is applied — FIFO over the debt or a set of specific invoices — and only then
 * picks invoices. The backend still rejects an invoice an active package covers
 * and any amount above the selection total.
 */
export function PagoFacturasPicker({
  facturas,
  cubiertas = [],
  loading = false,
  disabled = false,
  modoRequerido,
  totalSeleccionado,
  montoIngresado,
  onModeChange,
  onSelectionChange,
}: PagoFacturasPickerProps) {
  const t = useT();
  const { token } = theme.useToken();
  const form = Form.useFormInstance();
  const modo = Form.useWatch('modoFacturas', form) as ModoFacturas | undefined;
  const marcadas = (Form.useWatch('idFacturasPago', form) ?? []) as number[];

  if (!loading && facturas.length === 0 && cubiertas.length === 0) return null;

  // Surplus guidance (S5): the amount may pass what is marked only when no
  // payable invoice is left to mark — money does not become credit while debt
  // that could pay it exists.
  const pendientesSinMarcar = facturas.filter((factura) => !marcadas.includes(factura.id));
  const totalMarcado = facturas
    .filter((factura) => marcadas.includes(factura.id))
    .reduce((acc, factura) => acc + Number(factura.saldoAntes ?? 0), 0);
  const sobrante = montoIngresado - totalMarcado;
  const haySobrante = modo === MODO_FACTURAS.PUNTUAL && sobrante > 0.005 && marcadas.length > 0;
  const puedeMarcarMas = haySobrante && pendientesSinMarcar.length > 0;
  const totalPendienteSinMarcar = pendientesSinMarcar.reduce(
    (acc, factura) => acc + Number(factura.saldoAntes ?? 0),
    0,
  );

  const etiquetaFactura = (
    factura: { id: number; tipoFactura?: string; fechaInicio: string; fechaFin: string },
    saldo: string,
  ) =>
    t('pages.pagos.facturas.invoice', {
      id: factura.id,
      tipo: t(
        factura.tipoFactura === INVOICE_TYPE.EXTRAORDINARIA
          ? 'pages.pagos.facturas.tipoExtraordinaria'
          : 'pages.pagos.facturas.tipoPeriodica',
      ),
      inicio: dayjs(factura.fechaInicio).format('DD/MM/YYYY'),
      fin: dayjs(factura.fechaFin).format('DD/MM/YYYY'),
      saldo: formatCurrency(Number(saldo)),
    });

  return (
    <div
      style={{
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusSM,
        padding: token.paddingSM,
      }}
    >
      <Text strong style={{ display: 'block', lineHeight: 1.4 }}>
        {t('pages.pagos.facturas.title')}
      </Text>
      {loading ? (
        <Spin size="small" description={t('pages.pagos.facturas.loading')}>
          <div style={{ minHeight: token.controlHeight }} />
        </Spin>
      ) : (
        <>
          <Text
            type="secondary"
            style={{
              display: 'block',
              fontSize: 12,
              lineHeight: 1.5,
              marginBottom: token.marginXS,
            }}
          >
            {t('pages.pagos.facturas.rule')}
          </Text>
          <Form.Item
            name="modoFacturas"
            style={{ marginBottom: token.marginXS }}
            rules={
              modoRequerido
                ? [{ required: true, message: t('pages.pagos.facturas.modoRequired') }]
                : []
            }
          >
            <Radio.Group
              onChange={onModeChange}
              disabled={disabled}
              style={{ display: 'grid', gap: token.marginXS }}
            >
              <Radio value={MODO_FACTURAS.FIFO}>{t('pages.pagos.facturas.modoFifo')}</Radio>
              <Radio value={MODO_FACTURAS.PUNTUAL}>{t('pages.pagos.facturas.modoPuntual')}</Radio>
            </Radio.Group>
          </Form.Item>

          {modo === MODO_FACTURAS.PUNTUAL ? (
            <>
              <Form.Item
                name="idFacturasPago"
                style={{ marginBottom: 0 }}
                rules={[{ required: true, message: t('pages.pagos.facturas.seleccionRequired') }]}
              >
                <Checkbox.Group
                  onChange={onSelectionChange}
                  disabled={disabled}
                  style={{ display: 'grid', gap: token.marginXS }}
                >
                  {facturas.map((factura) => (
                    <Checkbox key={factura.id} value={factura.id}>
                      {etiquetaFactura(
                        {
                          id: factura.id,
                          tipoFactura: factura.tipoFactura,
                          fechaInicio: factura.fechaInicio,
                          fechaFin: factura.fechaFin,
                        },
                        factura.saldoAntes,
                      )}
                    </Checkbox>
                  ))}
                  {cubiertas.map((cubierta) => (
                    <Checkbox key={cubierta.id} value={cubierta.id} disabled>
                      <span style={{ display: 'block' }}>
                        {etiquetaFactura(
                          {
                            id: cubierta.id,
                            tipoFactura: INVOICE_TYPE.PERIODICA,
                            fechaInicio: cubierta.fechaInicio,
                            fechaFin: cubierta.fechaFin,
                          },
                          cubierta.saldoAntes,
                        )}
                      </span>
                      <Text
                        type="secondary"
                        style={{ display: 'block', fontSize: 12, lineHeight: 1.4 }}
                      >
                        {t('pages.pagos.facturas.covered', { idPaquete: cubierta.idPaquete })}
                      </Text>
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
              {totalSeleccionado > 0 ? (
                <Text
                  type="secondary"
                  aria-live="polite"
                  style={{ display: 'block', marginTop: token.marginXS, fontSize: 12 }}
                >
                  {t('pages.pagos.facturas.totalSeleccionado', {
                    total: formatCurrency(totalSeleccionado),
                  })}
                </Text>
              ) : null}
              {puedeMarcarMas ? (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    marginTop: token.marginXS,
                    display: 'grid',
                    gap: 2,
                    justifyItems: 'start',
                  }}
                >
                  <Text type="warning" style={{ fontSize: 12, lineHeight: 1.5 }}>
                    {t('pages.pagos.facturas.sobranteConPendientes', {
                      sobrante: formatCurrency(sobrante),
                    })}
                  </Text>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0 }}
                    onClick={() =>
                      form.setFieldValue(
                        'idFacturasPago',
                        [...marcadas, ...pendientesSinMarcar.map((factura) => factura.id)].sort(
                          (a, b) => a - b,
                        ),
                      )
                    }
                  >
                    {t('pages.pagos.facturas.marcarPendientes', {
                      monto: formatCurrency(totalPendienteSinMarcar),
                    })}
                  </Button>
                </div>
              ) : haySobrante ? (
                <Text
                  type="secondary"
                  aria-live="polite"
                  style={{
                    display: 'block',
                    marginTop: token.marginXS,
                    fontSize: 12,
                    lineHeight: 1.5,
                  }}
                >
                  {t('pages.pagos.facturas.sobranteSinPendientes', {
                    sobrante: formatCurrency(sobrante),
                  })}
                </Text>
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
